import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CommandDialog, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem, CommandSeparator,
} from './ui/command';
import { Package, Plus, ArrowRight, Loader2 } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { statusTone, statusLabel } from '../utils/statusTone';

// ⌘K / Ctrl+K: jump to any page, run a common action, or find an order by
// number, customer name, phone or tracking number.
export const CommandPalette = ({ open, onOpenChange, sections }) => {
  const navigate = useNavigate();
  const { can } = useAuth();
  const [q, setQ] = useState('');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (!open) { setQ(''); setOrders([]); } }, [open]);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 3 || !can('orders')) { setOrders([]); return undefined; }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await api.get('/orders/', { params: { search: term, limit: 8 } });
        const rows = Array.isArray(res.data) ? res.data : (res.data?.items || res.data?.orders || []);
        setOrders(rows.slice(0, 8));
      } catch (err) { setOrders([]); }
      finally { setLoading(false); }
    }, 220);
    return () => clearTimeout(t);
  }, [q, can]);

  const go = (href) => { onOpenChange(false); navigate(href); };
  const pages = useMemo(() => sections.flatMap((s) => s.items.map((i) => ({ ...i, group: s.title }))), [sections]);

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput placeholder="Search orders, customers, phone, AWB — or jump to a page…" value={q} onValueChange={setQ} />
      <CommandList className="max-h-[420px]">
        <CommandEmpty>{loading ? 'Searching…' : 'Nothing found.'}</CommandEmpty>
        {(orders.length > 0 || loading) && (
          <CommandGroup heading="Orders">
            {loading && !orders.length && (
              <CommandItem disabled value="__loading"><Loader2 className="mr-2 h-4 w-4 animate-spin" />Searching orders…</CommandItem>
            )}
            {orders.map((o) => (
              <CommandItem key={o.id} value={`order ${o.order_number} ${o.customer_name} ${o.phone} ${q}`} onSelect={() => go(`/orders/${o.id}`)}>
                <Package className="mr-2 h-4 w-4 text-muted-foreground" />
                <span className="font-mono text-sm">{o.order_number}</span>
                <span className="ml-2 truncate text-muted-foreground">{o.customer_name}</span>
                <span className={`ml-auto shrink-0 rounded-full border px-2 py-0.5 text-[11px] capitalize ${statusTone(o.status)}`}>{statusLabel(o.status)}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        <CommandGroup heading="Actions">
          {can('orders', 'edit') && <CommandItem value="new order create" onSelect={() => go('/orders/new')}><Plus className="mr-2 h-4 w-4" />New order</CommandItem>}
          {can('inventory') && <CommandItem value="stock count upload sheet" onSelect={() => go('/inventory')}><ArrowRight className="mr-2 h-4 w-4" />Inventory overview</CommandItem>}
          {can('logistics') && <CommandItem value="courier updates tracking apply" onSelect={() => go('/logistics/tracking')}><ArrowRight className="mr-2 h-4 w-4" />Apply courier updates</CommandItem>}
        </CommandGroup>
        <CommandSeparator />
        <CommandGroup heading="Go to">
          {pages.map((p) => {
            const Icon = p.icon;
            return (
              <CommandItem key={p.href} value={`${p.group} ${p.name}`} onSelect={() => go(p.href)}>
                <Icon className="mr-2 h-4 w-4 text-muted-foreground" />{p.name}
                <span className="ml-auto text-xs text-muted-foreground">{p.group}</span>
              </CommandItem>
            );
          })}
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
};

export default CommandPalette;
